// Sprint 17 A9: Hook for lead scoring model builder
import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useApiMutation } from "./use-api";

export interface ScoringCriterion {
  id: string;
  name: string;
  category: "engagement" | "demographic" | "behavioral" | "firmographic";
  weight: number; // 0-100
  operator: "equals" | "contains" | "greater_than" | "less_than" | "exists";
  value?: string;
  points: number;
  enabled: boolean;
}

export interface ScoringModel {
  id: string;
  name: string;
  description: string;
  criteria: ScoringCriterion[];
  maxScore: number;
  hotThreshold: number;
  warmThreshold: number;
  isActive: boolean;
  updatedAt: string;
}

export interface CrmProgramScoreBand {
  key: string;
  label: string;
  minScore: number;
}

export interface CrmProgramScoreSignal {
  key: string;
  label: string;
  componentKey?: string;
  factKey: string;
  points: number;
  reasonCode: string;
  description: string;
  condition?: unknown;
  pointModel?: unknown;
  emitReason?: boolean;
}

export interface CrmProgramScoreComponent {
  key: string;
  label: string;
  maxScore: number;
  signals: CrmProgramScoreSignal[];
}

export interface CrmProgramIntentRule {
  key: string;
  intentClass: string;
  label: string;
  when: string;
  priority: number;
  condition?: unknown;
}

export interface CrmProgramQualificationGate {
  key: string;
  label: string;
  when: string;
  effect: "allow" | "soft_cap" | "hard_cap";
  condition?: unknown;
  maxScore?: number;
  reasonCode?: string;
}

export interface CrmProgramTaskRule {
  key: string;
  triggerIntent: string;
  actionRef: string;
  templateKey: string;
  title: string;
  description?: string;
  taskType?: string;
  priority?: string;
  dueInHours: number;
  nextAction?: string;
  scriptTemplate?: string;
}

export interface CrmProgramProjectionRule {
  key: string;
  targetField: string;
  source:
    | "score"
    | "score_band"
    | "intent"
    | "qualification_gate"
    | "attribution"
    | "decision_digest"
    | "migration_context";
  valueKey?: string;
  valueMap?: Record<string, string | number | boolean | null>;
  piiSafe: boolean;
}

export interface CrmProgramConfig {
  source: "default" | "saved";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  scoreRange: { min: number; max: number };
  scoreBands: CrmProgramScoreBand[];
  scoreComponents: CrmProgramScoreComponent[];
  intentRules: CrmProgramIntentRule[];
  qualificationGates?: CrmProgramQualificationGate[];
  taskRules: CrmProgramTaskRule[];
  personProjectionRules: CrmProgramProjectionRule[];
  eventContract: {
    sourceEventFamilies: string[];
    projectedEventTypes: string[];
  };
}

export type CrmProgramConfigPayload = Omit<CrmProgramConfig, "source">;

export interface CrmProgramFactCoverage {
  configuredFactKeys: string[];
  suppliedFactKeys: string[];
  missingConfiguredFactKeys: string[];
  unusedSuppliedFactKeys: string[];
}

export interface CrmProgramPreviewEvidence {
  version: "stero.crm.program-policy-preview-evidence.v1";
  configDigestSha256: string;
  coverageStatus: "complete" | "complete_with_unused_supplied_facts" | "missing_configured_facts";
  decisionSummary: {
    score: number;
    scoreBeforeQualification: number;
    scoreBand: string;
    intentClass: string;
    matchedIntentRuleKey: string;
    qualificationGate: "unknown" | "pass" | "soft_cap" | "hard_cap";
    componentScores: Record<string, number>;
  };
  matchedSignalKeys: string[];
  matchedReasonCodes: string[];
  matchedQualificationGateKeys?: string[];
  matchedQualificationGateReasonCodes?: string[];
  taskRecommendationKeys: string[];
  projectionRuleKeys: string[];
  counts: {
    configuredFactKeys: number;
    suppliedFactKeys: number;
    missingConfiguredFactKeys: number;
    unusedSuppliedFactKeys: number;
    matchedSignals: number;
    matchedReasonCodes: number;
    matchedQualificationGates?: number;
    matchedQualificationGateReasonCodes?: number;
    taskRecommendations: number;
    projectionRules: number;
  };
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

export interface CrmProgramPolicyPreview {
  routeId: "stero.crm.program-policy-preview.v1";
  source: "default" | "saved" | "draft";
  configDigestSha256: string;
  decision: {
    score: number;
    scoreBeforeQualification: number;
    scoreBand: string;
    intentClass: string;
    matchedIntentRuleKey: string;
    qualificationGate: "unknown" | "pass" | "soft_cap" | "hard_cap";
    qualificationGateResults?: Array<{
      key: string;
      effect: "allow" | "soft_cap" | "hard_cap";
      matched: boolean;
      maxScore: number | null;
      appliedCapScore: number | null;
      reasonCode: string | null;
    }>;
    reasonCodes: string[];
    componentScores: Record<string, number>;
    signalScores: Array<{
      componentKey: string;
      signalKey: string;
      reasonCode: string;
      points: number;
    }>;
  };
  factKeys: string[];
  factCoverage: CrmProgramFactCoverage;
  previewEvidence: CrmProgramPreviewEvidence;
  previewEvidenceDigestSha256: string;
  taskRecommendations: Array<{
    key: string;
    actionRef: string;
    templateKey: string;
    title: string;
    description?: string | null;
    taskType?: string | null;
    priority?: string | null;
    dueInHours: number;
    nextAction?: string | null;
    hasScriptTemplate?: boolean;
  }>;
  projectionPreview: Array<{
    key: string;
    targetField: string;
    source: CrmProgramProjectionRule["source"];
    piiSafe: boolean;
  }>;
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

export interface CrmProgramPolicyReview {
  routeId: "stero.crm.program-policy-review.v1";
  version: "stero.crm.program-policy-review.v1";
  source: "default" | "saved" | "draft";
  status: "ready" | "needs_attention" | "blocked";
  counts: {
    scoreBands: number;
    scoreComponents: number;
    scoreSignals: number;
    intentRules: number;
    qualificationGates: number;
    taskRules: number;
    projectionRules: number;
    sourceEventFamilies: number;
    projectedEventTypes: number;
  };
  findings: Array<{
    severity: "error" | "warning" | "info";
    code: string;
    message: string;
    path?: string;
  }>;
  targetActionRefs: string[];
  unknownActionRefs: string[];
  manualReviewActionRefs: string[];
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

export type CrmProgramFactUsageKind =
  | "score_signal"
  | "score_condition"
  | "score_point_model"
  | "intent_condition"
  | "qualification_gate_condition";

export interface CrmProgramFactUsage {
  kind: CrmProgramFactUsageKind;
  path: string;
  label?: string;
  componentKey?: string;
  signalKey?: string;
  intentRuleKey?: string;
  qualificationGateKey?: string;
  reasonCode?: string;
  pointModelType?: "fixed" | "per_unit" | "sum";
}

export interface CrmProgramFactCatalogItem {
  factKey: string;
  usages: CrmProgramFactUsage[];
  consumers: {
    scoreComponents: string[];
    scoreSignals: string[];
    intentRules: string[];
    qualificationGates: string[];
  };
}

export interface CrmProgramFactCatalog {
  routeId: "stero.crm.program-policy-fact-catalog.v1";
  source: "default" | "saved";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  configDigestSha256: string;
  configuredFactKeys: string[];
  facts: CrmProgramFactCatalogItem[];
  counts: {
    facts: number;
    scoreSignalUsages: number;
    scoreConditionUsages: number;
    scorePointModelUsages: number;
    intentConditionUsages: number;
    qualificationGateConditionUsages: number;
  };
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

export function useScoringModel() {
  const [data, setData] = useState<ScoringModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    apiFetch<{ data: ScoringModel | null }>("/crm/intelligence/scoring/models")
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}

export function useCrmProgramConfig() {
  const [data, setData] = useState<CrmProgramConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    apiFetch<{ data: CrmProgramConfig }>("/crm/intelligence/program-config")
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load CRM program policy");
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}

export function useCrmProgramFactCatalog() {
  const [data, setData] = useState<CrmProgramFactCatalog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    apiFetch<{ data: CrmProgramFactCatalog }>("/crm/intelligence/program-config/fact-catalog")
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          setData(null);
          setError(null);
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load CRM program fact catalog");
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}

export function useSaveScoringModel() {
  return useApiMutation<Partial<ScoringModel>, ScoringModel>(
    "/crm/intelligence/scoring/models",
    "POST",
  );
}

export function useSaveCrmProgramConfig() {
  return useApiMutation<CrmProgramConfigPayload, { data: CrmProgramConfig }>(
    "/crm/intelligence/program-config",
    "PATCH",
  );
}

export function usePreviewCrmProgramConfig() {
  return useApiMutation<{
    facts: Record<string, unknown>;
    config?: CrmProgramConfigPayload;
  }, { data: CrmProgramPolicyPreview }>(
    "/crm/intelligence/program-config/preview",
    "POST",
  );
}

export function useReviewCrmProgramConfig() {
  return useApiMutation<{
    config?: CrmProgramConfigPayload;
  }, { data: CrmProgramPolicyReview }>(
    "/crm/intelligence/program-config/review",
    "POST",
  );
}
